/**
 * Heading - Multi-level heading component (H1-H6)
 *
 * Polymorphic heading component supporting all heading levels (H1-H6)
 * with responsive typography and theme integration.
 *
 * WHY this component:
 * - Consistent heading typography across entire app
 * - Theme-aware font properties via CSS custom properties
 * - Responsive font sizes for mobile devices
 * - Semantic HTML with proper heading hierarchy
 * - Centralized heading styles
 *
 * Use cases:
 * - Page titles (H1)
 * - Section headers (H2-H3)
 * - Subsection headers (H4-H5)
 * - Small headers (H6)
 *
 * Accessibility:
 * - Uses semantic HTML heading elements
 * - Maintains proper heading hierarchy
 * - Screen readers announce heading levels
 * - Responsive text prevents accessibility issues
 *
 * @see src/ui/Text.tsx - Body text component
 * @see src/ui/FormHeading.tsx - Form-specific headings
 */
import styled, { css } from 'styled-components';
import React from 'react';

/**
 * Heading component props interface
 */
export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Heading level (H1-H6) */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  /** Text color */
  color?: string;

  /** Center align text */
  $center?: boolean;

  /** Child content */
  children?: React.ReactNode;
}

/**
 * Styled polymorphic heading component
 *
 * WHY polymorphic 'as' prop:
 * - Single component for all heading levels
 * - Maintains semantic HTML hierarchy
 * - Simplifies heading management
 * - Reduces component count
 *
 * WHY CSS custom properties:
 * - Theme integration for multi-tenant support
 * - Channel-specific typography
 * - Easy theme switching
 * - Centralized design token management
 *
 * WHY responsive breakpoints (480px, 767px):
 * - 480px: Small phones (iPhone SE, older Android)
 * - 767px: Tablets and larger phones
 * - Progressive enhancement for larger screens
 *
 * WHY separate desktop and mobile custom properties:
 * - Different font sizes for different screen sizes
 * - Better readability on mobile
 * - Prevents text being too large on small screens
 * - More control over responsive typography
 *
 * H1 Styling:
 * - Largest heading for page titles
 * - Mobile: Reduced font size for small screens
 * - Theme font family for brand consistency
 *
 * H2 Styling:
 * - Section headers
 * - Mobile: Reduced size at 480px
 *
 * H3 Styling:
 * - Subsection headers
 * - Mobile: Scaled down for readability
 *
 * H4 Styling:
 * - Small section headers
 * - Uses specific H4 font family (might differ from theme)
 *
 * H5 Styling:
 * - Uppercase transformation for visual distinction
 * - Small, compact headers
 *
 * H6 Styling:
 * - Smallest heading level
 * - Uppercase for visual hierarchy
 *
 * WHY $center transient prop:
 * - Prevents passing to DOM (styled-components 5.1+)
 * - Cleaner HTML output
 * - Conditional text-align center
 */
const Heading = styled.h1<HeadingProps>`
  ${(props) =>
    css`
      color: ${props.color};
    `}
  ${(props) =>
    props.as === 'h1' &&
    css`
      /* font-family: var(--headings-h1-font-family); */
      font-family: var(--theme-font-family);
      font-size: var(--headings-h1-font-size);
      font-style: var(--headings-h1-font-style);
      font-weight: var(--headings-h1-font-weight);
      letter-spacing: var(--headings-h1-letter-spacing);
      line-height: var(--headings-h1-line-height);

      @media screen and (max-width: 480px) {
        font-size: var(--headings-h1-mobile-font-size);
        font-style: var(--headings-h1-mobile-font-style);
        font-weight: var(--headings-h1-mobile-font-weight);
        letter-spacing: var(--headings-h1-mobile--letter-spacing);
        line-height: var(--headings-h1-mobile-line-height);
        /* font-family: var(--headings-h1-mobile-font-family); */
        font-family: var(--theme-font-family);
      }

      @media (min-width: 480px) and (max-width: 767px) {
        font-size: var(--headings-h1-mobile-font-size);
        font-style: var(--headings-h1-mobile-font-style);
        font-weight: var(--headings-h1-mobile-font-weight);
        letter-spacing: var(--headings-h1-mobile--letter-spacing);
        line-height: var(--headings-h1-mobile-line-height);
        /* font-family: var(--headings-h1-mobile-font-family); */
        font-family: var(--theme-font-family);
      }
    `}

  ${(props) =>
    props.as === 'h2' &&
    css`
      /* font-family: var(--headings-h2-font-family); */
      font-family: var(--theme-font-family);
      font-size: var(--headings-h2-font-size);
      font-style: var(--headings-h2-font-style);
      font-weight: var(--headings-h2-font-weight);
      letter-spacing: var(--headings-h2-letter-spacing);
      line-height: var(--headings-h2-line-height);

      @media screen and (max-width: 480px) {
        font-size: var(--headings-h2-mobile-font-size);
        font-style: var(--headings-h2-mobile-font-style);
        font-weight: var(--headings-h2-mobile-font-weight);
        letter-spacing: var(--headings-h2-mobile--letter-spacing);
        line-height: var(--headings-h2-mobile-line-height);
        /* font-family: var(--headings-h2-mobile-font-family); */
        font-family: var(--theme-font-family);
      }
    `}

    ${(props) =>
    props.as === 'h3' &&
    css`
      /* font-family: var(--headings-h3-font-family); */
      font-family: var(--theme-font-family);
      font-size: var(--headings-h3-font-size);
      font-style: var(--headings-h3-font-style);
      font-weight: var(--headings-h3-font-weight);
      letter-spacing: var(--headings-h3-letter-spacing);
      line-height: var(--headings-h3-line-height);

      @media screen and (max-width: 480px) {
        font-size: var(--headings-h3-mobile-font-size);
        font-style: var(--headings-h3-mobile-font-style);
        font-weight: var(--headings-h3-mobile-font-weight);
        letter-spacing: var(--headings-h3-mobile--letter-spacing);
        line-height: var(--headings-h3-mobile-line-height);
        /* font-family: var(--headings-h3-mobile-font-family); */
        font-family: var(--theme-font-family);
      }
    `}


    ${(props) =>
    props.as === 'h4' &&
    css`
      font-family: var(--headings-h4-font-family);
      font-size: var(--headings-h4-font-size);
      font-style: var(--headings-h4-font-style);
      font-weight: var(--headings-h4-font-weight);
      letter-spacing: var(--headings-h4-letter-spacing);
      line-height: var(--headings-h4-line-height);

      @media screen and (max-width: 480px) {
        font-size: var(--headings-h4-mobile-font-size);
        font-style: var(--headings-h4-mobile-font-style);
        font-weight: var(--headings-h4-mobile-font-weight);
        letter-spacing: var(--headings-h4-mobile--letter-spacing);
        line-height: var(--headings-h4-mobile-line-height);
        font-family: var(--headings-h4-mobile-font-family);
      }
    `}

    ${(props) =>
    props.as === 'h5' &&
    css`
      font-family: var(--headings-h5-font-family);
      font-size: var(--headings-h5-font-size);
      font-style: var(--headings-h5-font-style);
      font-weight: var(--headings-h5-font-weight);
      letter-spacing: var(--headings-h5-letter-spacing);
      line-height: var(--headings-h5-line-height);
      text-transform: uppercase;

      @media screen and (max-width: 480px) {
        font-size: var(--headings-h5-mobile-font-size);
        font-style: var(--headings-h5-mobile-font-style);
        font-weight: var(--headings-h5-mobile-font-weight);
        letter-spacing: var(--headings-h5-mobile--letter-spacing);
        line-height: var(--headings-h5-mobile-line-height);
        font-family: var(--headings-h5-mobile-font-family);
      }
    `}

    ${(props) =>
    props.as === 'h6' &&
    css`
      font-family: var(--headings-h6-font-family);
      font-size: var(--headings-h6-font-size);
      font-style: var(--headings-h6-font-style);
      font-weight: var(--headings-h6-font-weight);
      letter-spacing: var(--headings-h6-letter-spacing);
      line-height: var(--headings-h6-line-height);
      text-transform: uppercase;

      @media screen and (max-width: 480px) {
        font-size: var(--headings-h6-mobile-font-size);
        font-style: var(--headings-h6-mobile-font-style);
        font-weight: var(--headings-h6-mobile-font-weight);
        letter-spacing: var(--headings-h6-mobile--letter-spacing);
        line-height: var(--headings-h6-mobile-line-height);
        font-family: var(--headings-h6-mobile-font-family);
      }
    `}

    ${(props) =>
    props.$center &&
    css`
      text-align: center;
    `}
`;

export default Heading;
